import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, fmtEur, fmtDate, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { COMPLAINT_STATUSES, SEVERITY_COLORS } from "../lib/fields";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CommentsAudit } from "../components/CommentsAudit";
import { Correspondence } from "../components/Correspondence";
import { LinkedSupplierComplaints } from "../components/LinkedSupplierComplaints";
import { SendEmailDialog } from "../components/SendEmailDialog";
import { EightDReport } from "../components/quality/EightDReport";
import { Ishikawa } from "../components/quality/Ishikawa";
import { CapaList } from "../components/quality/CapaList";
import { D_STEPS, FIVE_WHY_STEP } from "../lib/eightd";
import { toast } from "sonner";
import { ArrowLeft, AlertTriangle, CheckCircle2, Save, Mail, FileDown, Upload, Paperclip, Trash2, ShieldCheck } from "lucide-react";

export default function ComplaintDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, canWrite, plLabel } = useApp();
  const [c, setC] = useState(null);
  const [form, setForm] = useState({});
  const [emailOpen, setEmailOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [owners, setOwners] = useState([]);
  const [effNote, setEffNote] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get(`/complaints/${id}`).then((r) => { setC(r.data); setForm(r.data); }).catch(() => {});
  const loadFiles = () => api.get(`/files/complaints/${id}`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); loadFiles(); }, [id]);
  useEffect(() => { api.get("/users").then((r) => setOwners((r.data || []).map((u) => ({ id: u.id, name: u.name })))).catch(() => {}); }, []);

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/files/complaints/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("save") + " ✓");
      loadFiles();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm(t("delete_file_confirm"))) return;
    try {
      await api.delete(`/files/${fileId}`);
      toast.success(t("deleted") + " ✓");
      loadFiles();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const download8d = async () => {
    try {
      const token = localStorage.getItem("lf_token");
      const res = await fetch(`${API}/complaints/${id}/pdf-8d`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${c.complaint_number}_8D.pdf`;
      a.click();
    } catch { toast.error("PDF error"); }
  };

  if (!c) return <p className="text-gray-400">{t("loading")}</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (extra = {}) => {
    try {
      const r = await api.put(`/complaints/${id}`, { ...form, ...extra });
      setC(r.data); setForm(r.data);
      toast.success(t("save") + " ✓");
    } catch (e) { toast.error(errDetail(e)); }
  };

  const statusIdx = COMPLAINT_STATUSES.findIndex((s) => s === c.status || (c.status?.startsWith("Closed") && s.startsWith("Closed")));
  const filesLocked = c.status === "8D Report Sent" || c.status?.startsWith("Closed");
  const readonly8d = !canWrite || c.status?.startsWith("Closed");

  // Adapt flat customer-complaint fields <-> shared 8D value shape
  const meta = form.eight_d_meta || {};
  const stepValue = {};
  for (const s of D_STEPS) {
    const m = meta[s.key] || {};
    stepValue[s.key] = {
      content: form[s.legacy] || "",
      done: !!m.done, owner_id: m.owner_id || null, owner_name: m.owner_name || null, due: m.due || null,
      ...(s.key === FIVE_WHY_STEP ? { five_why: form.d4_five_why || "" } : {}),
    };
  }
  const onChangeStep = (key, patch) => {
    const step = D_STEPS.find((s) => s.key === key);
    setForm((f) => {
      const next = { ...f };
      if ("content" in patch) next[step.legacy] = patch.content;
      if ("five_why" in patch) next.d4_five_why = patch.five_why;
      const m = { ...(f.eight_d_meta || {}) };
      const mm = { ...(m[key] || {}) };
      ["done", "owner_id", "owner_name", "due"].forEach((k) => { if (k in patch) mm[k] = patch[k]; });
      m[key] = mm;
      next.eight_d_meta = m;
      return next;
    });
  };

  const eff = form.effectiveness || {};
  const verifyEffectiveness = async () => {
    try {
      await api.post(`/complaints/${id}/verify-effectiveness`, { note: effNote });
      toast.success(t("save") + " ✓");
      setEffNote("");
      load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="space-y-5" data-testid="complaint-detail-page">
      <button onClick={() => navigate("/complaints")} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1 transition-colors duration-150" data-testid="back-to-complaints">
        <ArrowLeft className="h-4 w-4" />{t("complaints")}
      </button>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-mono text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }} data-testid="complaint-number">
              {c.complaint_number}
              {c.sla_overdue && (
                <Badge className="bg-red-100 text-red-700 hover:bg-red-100 ml-3 align-middle" data-testid="sla-escalation-badge">
                  <AlertTriangle className="h-3 w-3 mr-1" />SLA {t("overdue")}
                </Badge>
              )}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-gray-500">
              <button className="text-blue-600 hover:underline" onClick={() => navigate(`/accounts/${c.account_id}`)}>{c.account_name}</button>
              ·<Badge className={`${SEVERITY_COLORS[c.severity] || ""} hover:bg-inherit`}>{c.severity}</Badge>
              ·<span>{plLabel("defect_categories", c.defect_category)}</span>·<span>{t("date_received")}: {fmtDate(c.date_received)}</span>·<span>{c.received_via}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold">{t("claimed_value")}</p>
            <p className="text-xl font-bold font-mono text-[#F5A623]">{fmtEur(c.claimed_value)}</p>
            <p className="text-xs text-gray-500 font-mono">{(c.claimed_quantity || 0).toLocaleString("de-DE")} pcs · {c.order_number}</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={download8d} data-testid="complaint-8d-pdf-btn">
            <FileDown className="h-3.5 w-3.5 mr-1" />{t("download_8d")}
          </Button>
          {canWrite && (
            <Button size="sm" className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => setEmailOpen(true)} data-testid="complaint-send-8d-btn">
              <Mail className="h-3.5 w-3.5 mr-1" />{t("send_8d")}
            </Button>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-1.5" data-testid="complaint-status-workflow">
          {COMPLAINT_STATUSES.map((s, i) => {
            const isCurrent = c.status === s;
            const done = i < statusIdx;
            return (
              <button key={s} disabled={!canWrite}
                data-testid={`status-step-${i}`}
                onClick={() => canWrite && save({ status: s })}
                className={`text-xs px-2.5 py-1.5 rounded-full border transition-colors duration-150 flex items-center gap-1 ${
                  isCurrent ? "bg-[#1B2A4A] text-white border-[#1B2A4A] font-semibold"
                    : done ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}>
                {done && <CheckCircle2 className="h-3 w-3" />}{s}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <EightDReport value={stepValue} onChangeStep={onChangeStep} disabled={readonly8d} owners={owners} />
            {canWrite && !c.status?.startsWith("Closed") && (
              <Button className="mt-4 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={() => save()} data-testid="complaint-save-btn">
                <Save className="h-4 w-4 mr-1.5" />{t("save")}
              </Button>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <Ishikawa value={form.ishikawa || {}} onChange={(v) => setForm((f) => ({ ...f, ishikawa: v }))} disabled={readonly8d} />
            {canWrite && !c.status?.startsWith("Closed") && (
              <Button size="sm" variant="outline" className="mt-3" onClick={() => save()} data-testid="ishikawa-save-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" />{t("save")}
              </Button>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <CapaList items={form.capa || []} onChange={(v) => setForm((f) => ({ ...f, capa: v }))} owners={owners} disabled={readonly8d} />
            {canWrite && !c.status?.startsWith("Closed") && (
              <Button size="sm" variant="outline" className="mt-3" onClick={() => save()} data-testid="capa-save-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" />{t("save")}
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="effectiveness-panel">
            <h3 className="text-sm font-semibold flex items-center gap-1.5 mb-3"><ShieldCheck className="h-3.5 w-3.5" />Wirksamkeit</h3>
            {eff.verified ? (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800" data-testid="effectiveness-verified">
                <CheckCircle2 className="h-4 w-4 inline mr-1" />Bestätigt von {eff.verified_by} ({(eff.verified_at || "").slice(0, 10)}): {eff.note}
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Pflicht vor „{COMPLAINT_STATUSES[6]}" (Abschluss angenommen).</p>
                {canWrite && !c.status?.startsWith("Closed") && (<>
                  <Textarea data-testid="eff-note-input" rows={2} value={effNote} onChange={(e) => setEffNote(e.target.value)} placeholder="Nachweis der Wirksamkeit…" />
                  <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" disabled={!effNote.trim()} onClick={verifyEffectiveness} data-testid="eff-verify-btn">
                    <ShieldCheck className="h-4 w-4 mr-1.5" />Wirksamkeit bestätigen
                  </Button>
                </>)}
              </div>
            )}
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <h3 className="text-sm font-semibold mb-3">{t("costs")}</h3>
            <div className="space-y-3">
              {[["cost_credit_note", t("cost_credit")], ["cost_reprint", t("cost_reprint")], ["cost_freight", t("cost_freight")],
                ["accepted_value", t("accepted_value")]].map(([k, lbl]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs text-gray-500">{lbl} (€)</Label>
                  <Input data-testid={`cost-${k}`} type="number" disabled={!canWrite} value={form[k] ?? 0}
                    onChange={(e) => set(k, Number(e.target.value))} className="h-9 font-mono" />
                </div>
              ))}
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("credit_note")}</Label>
                <Input data-testid="credit-note-input" disabled={!canWrite} value={form.credit_note_number || ""}
                  onChange={(e) => set("credit_note_number", e.target.value)} className="h-9 font-mono" />
              </div>
              <div className="flex justify-between pt-2 border-t text-sm font-semibold">
                <span>{t("total")}</span>
                <span className="font-mono">{fmtEur((Number(form.cost_credit_note) || 0) + (Number(form.cost_reprint) || 0) + (Number(form.cost_freight) || 0))}</span>
              </div>
              {canWrite && (
                <Button size="sm" className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white mt-1" onClick={() => save()} data-testid="complaint-costs-save-btn">
                  <Save className="h-4 w-4 mr-1.5" />{t("save_costs")}
                </Button>
              )}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-1.5"><Paperclip className="h-3.5 w-3.5" />{t("photos")}</h3>
              {canWrite && (<>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="complaint-upload-btn">
                  <Upload className="h-3 w-3 mr-1" />{t("upload_photo")}
                </Button>
              </>)}
            </div>
            {files.length === 0 && <p className="text-xs text-gray-400">–</p>}
            <div className="grid grid-cols-3 gap-2" data-testid="complaint-files">
              {files.map((f) => {
                const url = `${API}/files/download/${f.id}?auth=${localStorage.getItem("lf_token")}`;
                return (
                  <div key={f.id} className="relative group">
                    {f.content_type?.startsWith("image/") ? (
                      <a href={url} target="_blank" rel="noreferrer">
                        <img src={url} alt={f.original_filename} className="rounded-md border border-gray-200 object-cover h-20 w-full" />
                      </a>
                    ) : (
                      <a href={url} target="_blank" rel="noreferrer"
                        className="text-[11px] text-blue-600 underline flex items-center justify-center text-center h-20 border rounded-md px-1 break-all">
                        {f.original_filename}
                      </a>
                    )}
                    {canWrite && !filesLocked && (
                      <button onClick={() => deleteFile(f.id)} data-testid={`complaint-file-delete-${f.id}`}
                        title={t("delete")}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/90 border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-300 flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <CommentsAudit module="complaints" id={id} />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <Correspondence kind="customer" id={id} canWrite={canWrite} />
          </div>
        </div>
      </div>

      <LinkedSupplierComplaints complaintId={id} defectCategory={c.defect_category} />

      <SendEmailDialog open={emailOpen} onOpenChange={setEmailOpen}
        endpoint={`/complaints/${id}/send-8d`}
        defaultSubject={`8D-Report ${c.complaint_number} – Encasa`}
        onSent={load} />
    </div>
  );
}
